import { Avatar, FormControlLabel, FormLabel, Radio, RadioGroup, Snackbar } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import _ from 'lodash';
import React, { ChangeEvent, useEffect, useState } from 'react';
import AccordionSection from '../components/AccordionSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import PasswordValidator from '../components/PasswordValidator';
import { useGetEmployeeById } from '../services/react-query/employees/useGetEmployeeById';
import { FEATURE_FLAGS, useGetFeatureFlags } from '../services/react-query/users/useGetFeatureFlags';
import { IUserProfilePayload, useUpdateUserProfile } from '../services/react-query/users/useUpdateUserProfile';
import { inputContainerStyling, inputDividerStyling, itemContainerStyling, itemWrapperStyling } from '../styles/sxStyling';
import { Employee } from '../types/Employee';
import { UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { JsonifyFile } from '../utility/functions';
import { errorPopulator } from '../utility/inputUtils';

type FormData = {
  employee?: Employee;
  password: {
    currentPassword: string;
    newPassword: string;
    confirmedPassword: string;
  };
  emailReminder: string;
};

const BLANK_DATA: FormData = {
  password: {
    currentPassword: '',
    newPassword: '',
    confirmedPassword: '',
  },
  emailReminder: 'off',
};

const UserSettings: React.FC = () => {
  const [formData, setFormData] = useState(BLANK_DATA);
  const { employee, password, emailReminder } = formData;
  const passwordFilled = _.some(_.map(_.values(password), _.trim));

  const [saved, setSaved] = useState<boolean>(false);
  const [resetDisabled, setResetDisabled] = useState<boolean>(true);

  const { data: currentEmployeeData } = useGetEmployeeById({ uuid: 'current' });
  const { data: featureFlags } = useGetFeatureFlags();
  const { mutate: updateUserProfile } = useUpdateUserProfile();

  useEffect(() => {
    if (currentEmployeeData) {
      setFormData((prev) => ({ ...prev, employee: currentEmployeeData, emailReminder: currentEmployeeData.user_notifications?.reminder ?? 'off' }));
    }
  }, [currentEmployeeData]);

  if (!employee) return <Loading />;

  const updateEmployeeData = (event: ChangeEvent<HTMLInputElement>) => {
    if (!employee) return;

    const newEmployee = {
      ...employee,
      [event.target.id]: event.target.value,
    };
    setFormData({ ...formData, employee: newEmployee });
  };

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!employee) return;

    const file = _.first(event.target.files);
    if (!file) return;

    const jsonifiedFile = await JsonifyFile(file);
    setFormData({ ...formData, employee: { ...employee, avatar: jsonifiedFile } });
  };

  const updatePasswordData = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      password: {
        ...prev.password,
        [event.target.id]: event.target.value,
      },
    }));
  };

  const handleSave = () => {
    if (!employee) return;
    if (passwordFilled && resetDisabled) return;

    const updatePayload: IUserProfilePayload = {
      employee,
      notifications: { reminder: emailReminder },
    };

    // only try to update the password if the fields are filled in
    if (passwordFilled)
      updatePayload.password = {
        current_password: password.currentPassword,
        new_password: password.newPassword,
        confirmed_password: password.confirmedPassword,
      };

    updateUserProfile(updatePayload, { onSuccess: () => setSaved(true) });
  };

  return (
    <Box sx={itemWrapperStyling}>
      <ItemHeader
        title={employee.display_name}
        onSave={handleSave}
        icon={<Avatar src={employee.avatar_url} alt="avatar" sx={{ width: 30, height: 30 }} />}
        infoItems={[{ content: employee.title_value ?? '—' }]}
        disableActions={passwordFilled && resetDisabled}
      />

      <Box sx={itemContainerStyling}>
        <AccordionSection title="Personal Information" collapseId={UserSettingsCollapseKeys.USER_SETTINGS_PERSONAL}>
          <Box sx={inputContainerStyling}>
            <Box sx={inputDividerStyling}>
              <TextField
                id="chosen_name"
                label="Chosen Name"
                onChange={updateEmployeeData}
                value={employee?.chosen_name ?? ''}
                {...errorPopulator(employee, 'chosen_name')}
              />
              <TextField
                id="first_name"
                label="First Name"
                onChange={updateEmployeeData}
                value={employee?.first_name ?? ''}
                {...errorPopulator(employee, 'first_name')}
              />
              <TextField
                id="last_name"
                label="Last Name"
                onChange={updateEmployeeData}
                value={employee?.last_name ?? ''}
                {...errorPopulator(employee, 'last_name')}
              />
            </Box>
            <Box sx={inputDividerStyling}>
              <TextField id="email" label="Email" onChange={updateEmployeeData} value={employee?.email ?? ''} {...errorPopulator(employee, 'email')} />
              <TextField id="phone" label="Phone" onChange={updateEmployeeData} value={employee?.phone ?? ''} {...errorPopulator(employee, 'phone')} />
              <TextField
                type="file"
                slotProps={{
                  input: { inputProps: { accept: 'image/*' } },
                }}
                onChange={handleAvatarChange}
                sx={{ width: 1 }}
              />
            </Box>
          </Box>
        </AccordionSection>

        <AccordionSection title="Security" collapseId={UserSettingsCollapseKeys.USER_SETTINGS_SECURITY}>
          <Box sx={inputContainerStyling}>
            <Box sx={inputDividerStyling}>
              <TextField
                autoFocus
                required
                id="currentPassword"
                label="Current Password"
                type="password"
                onChange={updatePasswordData}
                value={password.currentPassword}
              />
              <TextField
                required
                id="newPassword"
                label="New Password"
                type="password"
                autoComplete="new-password"
                onChange={updatePasswordData}
                value={password.newPassword}
              />
              <TextField
                required
                id="confirmedPassword"
                label="Verify New Password"
                type="password"
                autoComplete="new-password"
                onChange={updatePasswordData}
                value={password.confirmedPassword}
              />
            </Box>

            <Box sx={inputDividerStyling}>
              <PasswordValidator
                password={password.newPassword}
                validatePassword={password.confirmedPassword}
                validCallback={(passwordValid) => setResetDisabled(!passwordValid)}
              />
            </Box>
          </Box>
        </AccordionSection>

        {_.get(featureFlags, FEATURE_FLAGS.notificationCentre) && (
          <AccordionSection title="Notifications" collapseId={UserSettingsCollapseKeys.USER_SETTINGS_NOTIFICATIONS}>
            <Box>
              <FormLabel>Email Reminders</FormLabel>
              <RadioGroup value={emailReminder} onChange={(_, value) => setFormData((prev) => ({ ...prev, emailReminder: value }))} row>
                <FormControlLabel value="daily" control={<Radio />} label="Daily" />
                <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
                <FormControlLabel value="off" control={<Radio />} label="Off" />
              </RadioGroup>
            </Box>
          </AccordionSection>
        )}
      </Box>

      <Snackbar open={saved} onClose={() => setSaved(false)} message="Updated!" />
    </Box>
  );
};

export default UserSettings;
